import React, { Suspense, lazy, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MortgageTermsSection } from './MortgageTermsSection';
import { MonthlyExpensesSection } from './MonthlyExpensesSection';
import { ExtraPaymentsSection } from './ExtraPaymentsSection';
import { AmortizationTable } from './AmortizationTable';

const KeyInsights = lazy(() => import('./KeyInsights').then((m) => ({ default: m.KeyInsights })));
import { useCalculatorStore } from '../../store/calculatorStore';
import { trackEvent } from '../../utils/analytics';
import type { MortgageTermsData } from '../../types/mortgageTypes';

interface MortgageCalculatorProps {
  tabId: string;
  onAddScenario: () => void;
  hasScenario2: boolean;
}

const isValidMortgageTermsForCalculation = (terms: MortgageTermsData) =>
  Number.isFinite(terms.homePrice) &&
  terms.homePrice > 0 &&
  Number.isFinite(terms.downPaymentAmount) &&
  terms.downPaymentAmount >= 0 &&
  Number.isFinite(terms.rate) &&
  terms.rate >= 0 &&
  Number.isFinite(terms.amortizationPeriod) &&
  terms.amortizationPeriod > 0 &&
  terms.startDate instanceof Date &&
  !isNaN(terms.startDate.getTime()) &&
  Boolean(terms.paymentFrequency);

const bucketCurrencyValue = (value: number): string => {
  if (value < 100_000) return '<100k';
  if (value < 250_000) return '100k-250k';
  if (value < 500_000) return '250k-500k';
  if (value < 750_000) return '500k-750k';
  if (value < 1_000_000) return '750k-1m';
  if (value < 1_500_000) return '1m-1.5m';
  return '1.5m+';
};

export const MortgageCalculator: React.FC<MortgageCalculatorProps> = ({
  tabId,
  onAddScenario,
  hasScenario2,
}) => {
  const calculatorTabs = useCalculatorStore(s => s.calculatorTabs);
  const calculateAmortizationSchedule = useCalculatorStore(s => s.calculateAmortizationSchedule);
  const hasUnsavedChanges = useCalculatorStore(s => s.hasUnsavedChanges);

  const tab = useMemo(
    () => calculatorTabs.find(t => t.id === tabId),
    [calculatorTabs, tabId]
  );

  const mortgageTerms = tab?.data.mortgageTerms;
  const annualExpenses = tab?.data.annualExpenses;
  const extraPayments = tab?.data.extraPayments;

  const shouldCalculate = useMemo(() => {
    if (!mortgageTerms) return false;
    return isValidMortgageTermsForCalculation(mortgageTerms);
  }, [mortgageTerms]);

  const handleCalculate = useCallback(() => {
    if (shouldCalculate && mortgageTerms && annualExpenses && extraPayments) {
      const terms = mortgageTerms;
      trackEvent('mortgage_calculator_start', {
        tab_id: tabId,
        loan_type_selected: terms.mortgageType,
        loan_program: terms.loanProgram,
        initial_property_value_range: bucketCurrencyValue(terms.homePrice),
        initial_deposit_range: bucketCurrencyValue(terms.downPaymentAmount),
      });
      calculateAmortizationSchedule(tabId);
      trackEvent('mortgage_comparison_view', {
        tab_id: tabId,
        page_type: 'calculator',
      });
    }
  }, [
    shouldCalculate,
    calculateAmortizationSchedule,
    mortgageTerms,
    annualExpenses,
    extraPayments,
    tabId,
  ]);

  useEffect(() => {
    handleCalculate();
  }, [handleCalculate]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  if (!tab) {
    return <div className="p-6 text-center text-ink-muted">Calculator not found</div>;
  }

  return (
    <div className="flex flex-col lg:flex-row gap-0 min-h-0">
      {/* ── Left: Form Sections ── */}
      <div className="flex-1 min-w-0 p-6 space-y-4">
        {/* Mortgage Terms */}
        <section data-tour="tour-mortgage-terms" className="bg-white rounded-2xl border border-stone-border shadow-card overflow-visible">
          <div className="flex items-center justify-between px-5 pt-4 pb-2">
            <div className="flex items-center gap-2">
              <span className="text-base">🏠</span>
              <h2 className="text-xs font-semibold text-ink-muted uppercase tracking-widest">
                Mortgage Terms
              </h2>
            </div>
          </div>
          <MortgageTermsSection tabId={tabId} />
        </section>

        {/* Monthly Expenses */}
        <section data-tour="tour-monthly-expenses" className="bg-white rounded-2xl border border-stone-border shadow-card overflow-hidden">
          <div className="flex items-center gap-2 px-5 pt-4 pb-2">
            <span className="text-base">⚡</span>
            <h2 className="text-xs font-semibold text-ink-muted uppercase tracking-widest">
              Monthly Homeowner Expenses
            </h2>
          </div>
          <MonthlyExpensesSection tabId={tabId} />
        </section>

        {/* Extra Payments */}
        <section data-tour="tour-extra-payments" className="bg-white rounded-2xl border border-stone-border shadow-card overflow-hidden">
          <div className="flex items-center gap-2 px-5 pt-4 pb-2">
            <span className="text-base">💳</span>
            <h2 className="text-xs font-semibold text-ink-muted uppercase tracking-widest">
              Extra Payments
            </h2>
            <span className="text-[10px] text-ink-faint ml-1">- Pay off faster &amp; save on interest</span>
          </div>
          <ExtraPaymentsSection tabId={tabId} />
        </section>

        {/* Amortization Table */}
        <section data-tour="tour-amortization-table" className="bg-white rounded-2xl border border-stone-border shadow-card overflow-hidden">
          <div className="flex items-center gap-2 px-5 pt-4 pb-2">
            <span className="text-base">📋</span>
            <h2 className="text-xs font-semibold text-ink-muted uppercase tracking-widest">
              Amortization Schedule
            </h2>
          </div>
          <div className="px-5 pb-2">
            <p className="text-xs text-ink-muted">
              Want to learn more?{' '}
              <Link to="/faq" className="text-gold-600 hover:underline">
                Read more on our FAQ page.
              </Link>
            </p>
          </div>
          <AmortizationTable tabId={tabId} />
        </section>
      </div>

      {/* ── Right: Live Payment Panel ── */}
      <div className="lg:w-80 xl:w-96 flex-shrink-0 p-6 lg:pl-0 flex flex-col gap-4">
        <div className="lg:sticky lg:top-32">
          <Suspense fallback={<div className="p-4 text-sm text-ink-muted" aria-hidden="true" />}>
            <KeyInsights
              tabId={tabId}
              onAddScenario={onAddScenario}
              hasScenario2={hasScenario2}
            />
          </Suspense>
        </div>
      </div>
    </div>
  );
};
